import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { MzicSvgComponent } from '@mzic/mzic-components';
import { LanguageService, PosthogAnalyticsService } from '@mzic/mzic-services';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
// THREE.js removed - only using native DOM for logo parallax
import { FooterComponent } from '../../components/footer/footer.component';
import { HeaderComponent } from '../../components/header/header.component';

// Registrar os plugins
gsap.registerPlugin(ScrollTrigger, SplitText);

const MISSION_TITLE_KEY = 'lang.mission.title';

@Component({
  selector: 'mzic-about',
  imports: [
    CommonModule,
    TranslocoModule,
    HeaderComponent,
    FooterComponent,
    MzicSvgComponent,
  ],
  templateUrl: './about.component.html',
  styleUrl: './about.component.scss',
  providers: [
    provideTranslocoScope({
      scope: 'about',
      alias: 'lang',
    }),
  ],
})
export class AboutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('wavesCanvas', { static: false })
  wavesCanvas!: ElementRef<HTMLDivElement>;
  @ViewChild('heroLogo', { static: false })
  heroLogo!: ElementRef<HTMLImageElement>;

  languageService = inject(LanguageService);
  activatedRoute = inject(ActivatedRoute);
  translocoService = inject(TranslocoService);
  router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private analyticsService = inject(PosthogAnalyticsService);
  private cdr = inject(ChangeDetectorRef);

  private animationFrameId?: number;
  private logoParallax = { x: 0, y: 0, targetX: 0, targetY: 0 };
  private resizeTimeout?: ReturnType<typeof setTimeout>;
  private splitTextInstance?: SplitText;
  showMissionTitle = true;

  // Breakpoint constants
  private readonly BREAKPOINTS = {
    XSMALL: 500,
    MOBILE: 1279,
    TABLET: 1400,
    DESKTOP: 1920,
  } as const;

  // Animation constants
  private readonly ANIMATION_CONFIG = {
    LINE_STAGGER: 1.2,
    LINE_DURATION: 1,
    RESIZE_DEBOUNCE: 300,
  } as const;

  /**
   * Detecta o breakpoint atual baseado na largura da viewport
   */
  private getBreakpoint(): 'xsmall' | 'mobile' | 'tablet' | 'desktop' | 'fullhd' {
    const width = window.innerWidth;
    if (width < this.BREAKPOINTS.XSMALL) return 'xsmall';
    if (width < this.BREAKPOINTS.MOBILE) return 'mobile';
    if (width < this.BREAKPOINTS.TABLET) return 'tablet';
    if (width < this.BREAKPOINTS.DESKTOP) return 'desktop';
    return 'fullhd';
  }

  /**
   * Retorna o valor apropriado para o breakpoint atual
   */
  private getResponsiveValue<T>(values: {
    xsmall: T;
    mobile: T;
    tablet: T;
    desktop: T;
    fullhd: T;
  }): T {
    return values[this.getBreakpoint()];
  }

  /**
   * Converte porcentagem da altura da viewport para pixels
   */
  private vh(percent: number): number {
    return (window.innerHeight * percent) / 100;
  }

  /**
   * Verifica se o dispositivo é mobile ou menor
   */
  private isMobileOrSmaller(): boolean {
    const breakpoint = this.getBreakpoint();
    return breakpoint === 'mobile' || breakpoint === 'xsmall';
  }

  constructor() {
    effect(() => {
      const lang = this.languageService.languageState();
      const current = this.router.url.split('/').pop();
      if (lang !== '') {
        this.router.navigate([`${lang}/${current}`]);
      }
    });
  }

  ngOnInit() {
    this.setLanguageFromRoute();
    this.analyticsService.trackPageView('about', {
      language: this.languageService.languageState(),
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.initializeGSAPAnimations();
      this.initializeThreeJS();
      this.setupResizeListener();
    }, 100);
  }

  ngOnDestroy() {
    this.analyticsService.clearAllTimers();
    this.resetTitleAnimation();
    this.cleanupThreeJS();
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  private currentLang = '';

  setLanguageFromRoute() {
    this.activatedRoute.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const lang = params['lang'];

        if (lang) {
          const previousLang = this.currentLang;
          const languageChanged = Boolean(previousLang) && previousLang !== lang;

          if (languageChanged) {
            this.resetTitleAnimation();
          }

          this.translocoService.setActiveLang(lang);
          this.languageService.languageState.set(lang);
          this.currentLang = lang;

          // Só reinicializa se o idioma realmente mudou
          if (languageChanged) {
            const scopeLang = `about/${lang}`;

            this.translocoService
              .load(scopeLang)
              .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError(() => {
                  return of(null);
                }),
                take(1),
              )
              .subscribe(() => {
                this.translocoService.translate(MISSION_TITLE_KEY, {}, lang);
                this.showMissionTitle = false;
                this.cdr.detectChanges();
                this.showMissionTitle = true;
                this.cdr.detectChanges();
                // Aguardar o próximo frame de renderização do Angular
                requestAnimationFrame(() => {
                  // Aguardar mais um frame para garantir que o DOM foi atualizado
                  requestAnimationFrame(() => {
                    this.initializeGSAPAnimations();
                  });
                });
              });
          }
        }
      });
  }

  private resetTitleAnimation() {
    if (this.splitTextInstance) {
      this.splitTextInstance.revert();
      this.splitTextInstance = undefined;
    }

    this.killAboutScrollTriggers();
  }

  private killAboutScrollTriggers() {
    const triggerIds = new Set(['header-pin', 'lines-animation']);
    ScrollTrigger.getAll().forEach((trigger) => {
      if (triggerIds.has(trigger.vars?.id as string)) {
        trigger.kill();
      }
    });
  }

  private initializeGSAPAnimations() {
    // Reverter o split anterior se existir
    if (this.splitTextInstance) {
      this.splitTextInstance.revert();
    }

    // Criar novo split
    this.splitTextInstance = new SplitText('.content-title', { type: 'lines' });
    const split = this.splitTextInstance;

    const viewportHeight = window.innerHeight;
    const isMobile = this.isMobileOrSmaller();

    // Configuração de ScrollTrigger responsiva
    const scrollTriggerStart = this.getResponsiveValue({
      xsmall: 'top 80%',
      mobile: 'top 65%',
      tablet: 'top 18%',
      desktop: 'top 18%',
      fullhd: 'top 18%',
    });

    const scrollTriggerEnd = this.getResponsiveValue({
      xsmall: 'bottom 80%',
      mobile: 'bottom 65%',
      tablet: 'bottom 22%',
      desktop: 'bottom 22%',
      fullhd: 'bottom 25%',
    });

    const headerPinEnd = this.getResponsiveValue({
      xsmall: '+=20%',
      mobile: '+=20%',
      tablet: '+=75%',
      desktop: '+=85%',
      fullhd: '+=90%',
    });

    // Posição final das linhas (valores proporcionais ao viewport em mobile, fixos em desktop)
    const finalYPosition = this.getResponsiveValue({
      xsmall: 0,
      mobile: 0,
      tablet: this.vh(10),
      desktop: this.vh(10),
      fullhd: 100,
    });

    // Posição final do conteúdo da direita (apenas para tablet+)
    const rightContentY = this.getResponsiveValue({
      xsmall: this.vh(40),
      mobile: this.vh(50),
      tablet: this.vh(55),
      desktop: 550,
      fullhd: 560,
    });

    // Configurar posições iniciais
    this.setupInitialPositions(split.lines, viewportHeight);

    // Criar ScrollTriggers
    this.createHeaderPinTrigger(headerPinEnd);
    const linesTl = this.createLinesTimeline(scrollTriggerStart, scrollTriggerEnd, isMobile);

    // Animar linhas
    this.animateLines(linesTl, split.lines, finalYPosition);

    // Animar parágrafo da direita
    this.animateRightContent(linesTl, split.lines.length, rightContentY, isMobile);
  }

  /**
   * Configura as posições iniciais dos elementos antes das animações
   */
  private setupInitialPositions(lines: Element[], viewportHeight: number) {
    // Todas as linhas começam fora da viewport (embaixo)
    gsap.set(lines, { y: viewportHeight });

    // Parágrafo da direita também começa fora da viewport (mobile e desktop)
    gsap.set('.demo .content.right', { y: viewportHeight });
  }

  /**
   * Cria o ScrollTrigger para fixar o header durante as animações
   */
  private createHeaderPinTrigger(headerPinEnd: string) {
    ScrollTrigger.create({
      trigger: '.header-waves-container',
      start: 'top top',
      end: headerPinEnd,
      pin: true,
      pinSpacing: false,
      id: 'header-pin',
    });
  }

  /**
   * Cria a timeline principal para animação das linhas
   */
  private createLinesTimeline(start: string, end: string, isMobile: boolean) {
    return gsap.timeline({
      scrollTrigger: {
        trigger: '.content-title',
        start,
        end,
        scrub: 1,
        pin: !isMobile,
        id: 'lines-animation',
      },
    });
  }

  /**
   * Anima cada linha do título em sequência
   */
  private animateLines(timeline: gsap.core.Timeline, lines: Element[], finalY: number) {
    lines.forEach((line, index) => {
      timeline.to(
        line,
        {
          y: finalY,
          duration: this.ANIMATION_CONFIG.LINE_DURATION,
          ease: 'sine.inOut',
        },
        index * this.ANIMATION_CONFIG.LINE_STAGGER,
      );
    });
  }

  /**
   * Anima o conteúdo da direita após as linhas
   * Mobile: anima para y: 0 (posição original)
   * Desktop: anima para valor responsivo
   */
  private animateRightContent(timeline: gsap.core.Timeline, numLines: number, finalY: number, isMobile: boolean) {
    const lastLineTime = (numLines - 1) * this.ANIMATION_CONFIG.LINE_STAGGER + 1;
    const targetY = isMobile ? 0 : finalY;

    timeline.to(
      '.demo .content.right',
      {
        y: targetY,
        duration: this.ANIMATION_CONFIG.LINE_DURATION,
        ease: 'sine.inOut',
      },
      lastLineTime,
    );
  }

  private initializeThreeJS() {
    if (!this.wavesCanvas) return;

    const container = this.wavesCanvas.nativeElement;

    // Configuration matching Figma design
    const C = {
      W: container.clientWidth,
      H: container.clientHeight,
      color: 0x9655ff, // Exact Figma purple
      // Wave gap configuration
      gapMin: 12, // Top gap in pixels
      gapMax: 63, // Bottom gap in pixels
      // Calculate number of waves that fit in viewport with progressive gaps
      // Using arithmetic series: sum = (n-1) * (gapMin + gapMax) / 2
      // where n-1 is the number of gaps (n waves create n-1 gaps)
      // Solving for n: n = (2 * H) / (gapMin + gapMax) + 1
      // Add 20% buffer to ensure we don't overflow bottom
      get lineCount() {
        const avgGap = (this.gapMin + this.gapMax) / 2;
        const availableHeight = this.H * 0.85; // Use 85% of height to prevent overflow
        const numGaps = Math.floor(availableHeight / avgGap);
        return numGaps + 1; // n waves = n-1 gaps + 1
      },
      // Wave configuration
      pointsPerLine: 500, // Very smooth curves
      lineWidth: 2.5, // Thicker by 1px
      lineOpacity: 0.65,
      // Dot configuration
      dotsPerGap: 150, // Dots per gap area
      dotSizeMin: 9.0, // 9x9px minimum
      dotSizeMax: 16.0, // 16x16px maximum
      dotOpacity: 0.75,
      // Animation
      animationSpeed: 0.08,
      noiseScale: 0.0015,
      // Noise strength also scaled to gap to prevent crossing
      get noiseStrength() {
        return this.gapMin * 0.15; // 15% of minimum gap
      },
      flowSpeed: 0.25,
      // Wave flow parameters
      // Amplitude must be less than half minimum gap to prevent crossing
      get waveAmplitude() {
        return this.gapMin * 0.35; // 35% of minimum gap = safe margin
      },
      waveFrequency: 0.007,
      // Parallax
      parallaxStrength: 18,
      parallaxSmoothing: 0.1,
    };

    // Three.js removed - only using native mouse tracking for logo parallax

    // Mouse tracking for parallax
    const mouse = { x: 0, y: 0, targetX: 0, targetY: 0 };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      mouse.targetX = ((e.clientX - rect.left) / C.W - 0.5) * 2;
      mouse.targetY = -((e.clientY - rect.top) / C.H - 0.5) * 2;

      // Logo parallax (opposite direction for depth effect)
      this.logoParallax.targetX = -mouse.targetX * 15;
      this.logoParallax.targetY = -mouse.targetY * 15;
    };

    container.addEventListener('mousemove', handleMouseMove);

    // Animation loop
    const updateLogoParallax = () => {
      // Smooth logo parallax
      this.logoParallax.x +=
        (this.logoParallax.targetX - this.logoParallax.x) * C.parallaxSmoothing;
      this.logoParallax.y +=
        (this.logoParallax.targetY - this.logoParallax.y) * C.parallaxSmoothing;

      if (this.heroLogo?.nativeElement) {
        this.heroLogo.nativeElement.style.transform = `translate(${this.logoParallax.x}px, ${this.logoParallax.y}px)`;
      }
    };

    const render = () => {
      updateLogoParallax();
      // No need to render Three.js scene anymore - just update logo
      this.animationFrameId = requestAnimationFrame(render);
    };
    render();

    // Handle resize (simplified - no Three.js rendering needed)
    const handleResize = () => {
      if (!container) return;
      const width = container.clientWidth;
      const height = container.clientHeight;
      C.W = width;
      C.H = height;
    };

    window.addEventListener('resize', handleResize);
  }

  /**
   * Configura listener de resize com debounce para recalcular animações
   */
  private setupResizeListener() {
    const handleResize = () => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        this.killAboutScrollTriggers();
        this.initializeGSAPAnimations();
      }, this.ANIMATION_CONFIG.RESIZE_DEBOUNCE);
    };

    window.addEventListener('resize', handleResize);

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('resize', handleResize);
    });
  }

  /**
   * Limpa recursos do Three.JS ao destruir o componente
   */
  private cleanupThreeJS() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }
}
